import type {
  AdminUser,
  AdminUserDetail,
  Appearance,
  DMConversation,
  DMMessage,
  DMUser,
  Dossier,
  PublicDossier,
  Team,
  LeaderboardResponse,
  HealthCheck,
  ModAction,
  OnlinePlayer,
  Page,
  PublicUser,
  Role,
  ScoreResult,
  VoiceTestToken,
} from "./types";

/** Erro de API com os detalhes por campo devolvidos pelo backend. */
export class ApiError extends Error {
  readonly status: number;
  readonly fields: Record<string, string>;
  readonly code: string;

  constructor(status: number, code: string, message: string, fields: Record<string, string> = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

interface ErrorBody {
  error?: string;
  message?: string;
  fields?: Record<string, string>;
}

async function request<T>(path: string, init: RequestInit = {}, token?: string): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  let response: Response;
  try {
    response = await fetch(path, { ...init, headers });
  } catch {
    throw new ApiError(0, "network", "Não foi possível falar com o servidor. Ele está rodando?");
  }

  if (response.status === 204) return undefined as T;

  const body = (await response.json().catch(() => ({}))) as T & ErrorBody;

  if (!response.ok) {
    const code = body.error ?? "erro";
    const message = body.message ?? (body.error || `Erro ${response.status}`);
    throw new ApiError(response.status, code, message, body.fields ?? {});
  }
  return body as T;
}

/**
 * Com a confirmacao de e-mail opcional, o cadastro ja devolve a sessao. Com
 * ela obrigatoria (`verificationRequired`), token e expiresAt nao vem: a
 * pessoa precisa clicar no link antes do login.
 */
export interface RegisterResult {
  token?: string;
  expiresAt?: string;
  verificationRequired: boolean;
  user: PublicUser;
  emailSent: boolean;
  message: string;
}

export interface LoginResult {
  token: string;
  expiresAt: string;
  user: PublicUser;
}

/** O que esta liberado antes do login (GET /api/meta). */
export interface ServerMeta {
  /** Dominios aceitos no cadastro; vazio = qualquer um. */
  emailDomains: string[];
  emailVerificationRequired: boolean;
}

/** Login recusado porque o e-mail ainda nao foi confirmado. */
export const EMAIL_NOT_VERIFIED = "email_not_verified";

export const api = {
  meta() {
    return request<ServerMeta>("/api/meta");
  },

  register(email: string, password: string, passwordConfirm: string, remember: boolean) {
    return request<RegisterResult>("/api/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, passwordConfirm, remember }),
    });
  },

  login(login: string, password: string, remember: boolean) {
    return request<LoginResult>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ login, password, remember }),
    });
  },

  resendActivation(email: string) {
    return request<{ message: string }>("/api/auth/resend", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  forgotPassword(email: string) {
    return request<{ message: string }>("/api/auth/forgot", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  resetPassword(token: string, newPassword: string, newPasswordConfirm: string) {
    return request<{ message: string }>("/api/auth/reset", {
      method: "POST",
      body: JSON.stringify({ token, newPassword, newPasswordConfirm }),
    });
  },

  /** Reenvia a confirmacao de e-mail da propria conta (botao do perfil). */
  resendVerification(token: string) {
    return request<{ message: string }>("/api/me/verification", { method: "POST" }, token);
  },

  me(token: string) {
    return request<{ user: PublicUser; active: boolean }>("/api/me", {}, token);
  },

  leaderboard(slug: string, token?: string, limit = 10) {
    return request<LeaderboardResponse>(
      `/api/games/${encodeURIComponent(slug)}/leaderboard?limit=${limit}`,
      {},
      token,
    );
  },

  submitScore(slug: string, token: string, score: number, durationMs: number) {
    return request<ScoreResult>(
      `/api/games/${encodeURIComponent(slug)}/scores`,
      { method: "POST", body: JSON.stringify({ score, durationMs }) },
      token,
    );
  },

  updateDisplayName(token: string, displayName: string) {
    return request<{ user: PublicUser }>(
      "/api/me/name",
      { method: "PATCH", body: JSON.stringify({ displayName }) },
      token,
    );
  },

  updateAppearance(token: string, appearance: Appearance) {
    return request<{ user: PublicUser }>(
      "/api/me/appearance",
      { method: "PATCH", body: JSON.stringify(appearance) },
      token,
    );
  },

  /** Quem esta no Jardim agora (qualquer jogador logado). */
  playersOnline(token: string) {
    return request<{ players: { id: string; name: string }[] }>("/api/players/online", {}, token);
  },

  /** O proprio dossie, para editar, e a lista de times. */
  myDossier(token: string) {
    return request<{ dossier: Dossier; teams: Team[] }>("/api/me/dossier", {}, token);
  },

  updateDossier(token: string, dossier: Dossier) {
    return request<{ dossier: Dossier; message: string }>(
      "/api/me/dossier",
      { method: "PUT", body: JSON.stringify(dossier) },
      token,
    );
  },

  /** O dossie de alguem como os outros veem (o proprio vem completo). */
  dossier(token: string, userId: string) {
    return request<{ dossier: PublicDossier }>(`/api/users/${encodeURIComponent(userId)}/dossier`, {}, token);
  },

  updatePassword(token: string, currentPassword: string, newPassword: string) {
    return request<{ message: string }>(
      "/api/me/password",
      { method: "PUT", body: JSON.stringify({ currentPassword, newPassword }) },
      token,
    );
  },
};

type Message = { message: string };
const post = (body?: object): RequestInit => ({ method: "POST", body: JSON.stringify(body ?? {}) });
const userPath = (prefix: string, id: string, rest = "") => `/api/${prefix}/users/${encodeURIComponent(id)}${rest}`;

export interface AccountChanges {
  username?: string;
  displayName?: string;
  email?: string;
}

/** Painel administrativo: so admins. */
export const adminApi = {
  online(token: string) {
    return request<{ players: OnlinePlayer[] }>("/api/admin/online", {}, token);
  },

  /** Desativa a conta (ex.: saiu da empresa): derruba e bloqueia o login. */
  deactivate(token: string, id: string, reason: string) {
    return request<Message>(userPath("admin", id, "/deactivate"), post({ reason }), token);
  },

  reactivate(token: string, id: string) {
    return request<Message>(userPath("admin", id, "/reactivate"), post(), token);
  },

  users(token: string, query: string, role: Role | "", page: number) {
    const params = new URLSearchParams({ q: query, role, page: String(page) });
    return request<Page & { users: AdminUser[] }>(`/api/admin/users?${params}`, {}, token);
  },

  user(token: string, id: string) {
    return request<AdminUserDetail>(userPath("admin", id), {}, token);
  },

  update(token: string, id: string, changes: AccountChanges) {
    return request<{ user: AdminUser }>(userPath("admin", id), { method: "PATCH", body: JSON.stringify(changes) }, token);
  },

  setPassword(token: string, id: string, newPassword: string) {
    return request<Message>(userPath("admin", id, "/password"), { method: "PUT", body: JSON.stringify({ newPassword }) }, token);
  },

  endSessions(token: string, id: string) {
    return request<Message>(userPath("admin", id, "/logout"), post(), token);
  },

  /** game vazio apaga de todos os jogos. */
  deleteScores(token: string, id: string, game = "") {
    const query = game ? `?game=${encodeURIComponent(game)}` : "";
    return request<{ deleted: number }>(userPath("admin", id, `/scores${query}`), { method: "DELETE" }, token);
  },

  deleteUser(token: string, id: string, confirmUsername: string) {
    return request<Message>(
      userPath("admin", id),
      { method: "DELETE", body: JSON.stringify({ confirmUsername }) },
      token,
    );
  },

  announce(token: string, message: string) {
    return request<Message>("/api/admin/announce", post({ message }), token);
  },

  actions(token: string, page: number) {
    return request<Page & { actions: ModAction[] }>(`/api/admin/actions?page=${page}`, {}, token);
  },

  /**
   * Checklist da configuracao; origin = o endereco pelo qual o painel foi
   * aberto. freshSMTP confere o SMTP agora (senao vale o resultado guardado).
   */
  health(token: string, origin: string, freshSMTP = false) {
    const params = new URLSearchParams({ origin, smtp: freshSMTP ? "1" : "" });
    return request<{ checks: HealthCheck[]; checkedAt: string }>(
      `/api/admin/health?${params}`,
      {},
      token,
    );
  },

  voiceTest(token: string) {
    return request<VoiceTestToken>("/api/admin/health/voice-test", post(), token);
  },

  teams(token: string) {
    return request<{ teams: Team[] }>("/api/admin/teams", {}, token);
  },

  createTeam(token: string, name: string) {
    return request<Message>("/api/admin/teams", post({ name }), token);
  },

  renameTeam(token: string, id: number, name: string) {
    return request<Message>(`/api/admin/teams/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }, token);
  },

  deleteTeam(token: string, id: number) {
    return request<Message>(`/api/admin/teams/${id}`, { method: "DELETE" }, token);
  },
};

const dmPath = (id: string, rest = "") => `/api/dm/conversations/${encodeURIComponent(id)}${rest}`;

/** Mensagens privadas: conversas a dois e grupos. */
export const dmApi = {
  searchUsers(token: string, query: string) {
    return request<{ users: DMUser[] }>(`/api/dm/users?q=${encodeURIComponent(query)}`, {}, token);
  },

  conversations(token: string) {
    return request<{ conversations: DMConversation[] }>("/api/dm/conversations", {}, token);
  },

  conversation(token: string, id: string) {
    return request<{ conversation: DMConversation }>(dmPath(id), {}, token);
  },

  /** Abre (ou cria) a conversa a dois com userId. */
  direct(token: string, userId: string) {
    return request<{ conversation: DMConversation }>("/api/dm/direct", post({ userId }), token);
  },

  createGroup(token: string, name: string, userIds: string[]) {
    return request<{ conversation: DMConversation }>("/api/dm/groups", post({ name, userIds }), token);
  },

  /** As mensagens anteriores a `before` (0 = as ultimas), em ordem cronologica. */
  messages(token: string, id: string, before = 0) {
    const query = before ? `?before=${before}` : "";
    return request<{ messages: DMMessage[]; more: boolean }>(dmPath(id, `/messages${query}`), {}, token);
  },

  send(token: string, id: string, text: string) {
    return request<{ message: DMMessage }>(dmPath(id, "/messages"), post({ text }), token);
  },

  markRead(token: string, id: string, messageId: number) {
    return request<void>(dmPath(id, "/read"), post({ messageId }), token);
  },

  rename(token: string, id: string, name: string) {
    return request<{ conversation: DMConversation }>(
      dmPath(id),
      { method: "PATCH", body: JSON.stringify({ name }) },
      token,
    );
  },

  addMembers(token: string, id: string, userIds: string[]) {
    return request<{ conversation: DMConversation }>(dmPath(id, "/members"), post({ userIds }), token);
  },

  leave(token: string, id: string) {
    return request<void>(dmPath(id, "/members/me"), { method: "DELETE" }, token);
  },
};
